import type { FC } from 'react';
import React, { memo, useMemo } from 'react';
import { Platform, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

type WebViewSource = { uri: string } | { html: string };

export type WebViewProps = {
  source?: WebViewSource;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  /**
   * Maps to iframe `allow` attribute.
   * Example: "camera; microphone; fullscreen"
   */
  allow?: string;
  /**
   * Maps to iframe `sandbox` attribute.
   * Example: "allow-scripts allow-same-origin"
   */
  sandbox?: string;
  /**
   * Maps to iframe `referrerPolicy` attribute.
   * Example: "no-referrer"
   */
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  onLoad?: () => void;
  onError?: (event: unknown) => void;
};

const WebViewComponent: FC<WebViewProps> = ({
  source,
  style,
  testID,
  allow,
  sandbox,
  referrerPolicy,
  onLoad,
  onError,
}) => {
  const iframeStyle = useMemo<React.CSSProperties>(
    () => ({
      border: 0,
      width: '100%',
      height: '100%',
    }),
    [],
  );

  const resolved = useMemo(() => {
    if (!source) return { kind: 'none' as const };
    if ('uri' in source) return { kind: 'uri' as const, uri: source.uri };
    return { kind: 'html' as const, html: source.html };
  }, [source]);

  if (resolved.kind === 'none') return null;

  // RN web renders <View> as a div; iframe needs CSS sizing.
  // `Platform.OS` guard is defensive; this file should only be used on web.
  if (Platform.OS !== 'web') return null;

  return (
    <View style={[styles.container, style]} testID={testID}>
      <iframe
        title={testID ?? 'webview'}
        src={resolved.kind === 'uri' ? resolved.uri : undefined}
        srcDoc={resolved.kind === 'html' ? resolved.html : undefined}
        style={iframeStyle}
        allow={allow}
        sandbox={sandbox}
        referrerPolicy={referrerPolicy}
        onLoad={() => onLoad?.()}
        onError={e => onError?.(e)}
      />
    </View>
  );
};

export const WebView = memo(WebViewComponent);

WebView.displayName = 'WebView';

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
});
